import { useState } from "react";
import { Link } from "react-router-dom";
import { FolderClosed, FolderInput, Loader2, MessageCircle, MoreHorizontal, Pencil, Search, SquarePen, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DeleteConversationDialog } from "@/components/chat/DeleteConversationDialog";
import { MoveToProjectDialog } from "@/components/projects/MoveToProjectDialog";
import { useConversations, useRenameConversation } from "@/hooks/queries";
import { useDebounce } from "@/hooks/useDebounce";
import { formatRelative } from "@/lib/format";

function RenameDialog({ conversation, onClose }) {
  const [title, setTitle] = useState(conversation?.title || "");
  const rename = useRenameConversation();
  const save = async (e) => {
    e.preventDefault();
    try {
      await rename.mutateAsync({ id: conversation.id, title: title.trim() });
      toast.success("Conversation renamed");
      onClose();
    } catch (err) {
      toast.error(err.message || "Couldn't rename the conversation");
    }
  };
  return (
    <Dialog open={Boolean(conversation)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="border-white/10 bg-ink-850 sm:max-w-md" data-testid="rename-conversation-dialog">
        <form onSubmit={save}>
          <DialogHeader>
            <DialogTitle className="text-white">Rename conversation</DialogTitle>
          </DialogHeader>
          <input
            autoFocus
            value={title}
            maxLength={200}
            onChange={(e) => setTitle(e.target.value)}
            data-testid="rename-conversation-input"
            className="mt-4 h-11 w-full rounded-xl border border-white/10 bg-ink-900 px-3 text-sm text-white focus:border-white/25 focus:outline-none"
          />
          <DialogFooter className="mt-5 gap-2">
            <button type="button" onClick={onClose} className="rounded-full border border-white/10 px-4 py-2 text-sm text-zinc-300 hover:bg-white/[0.05]" data-testid="rename-conversation-cancel">
              Cancel
            </button>
            <button type="submit" disabled={!title.trim() || rename.isPending} className="rounded-full bg-white px-4 py-2 text-sm font-medium text-ink-950 disabled:opacity-50" data-testid="rename-conversation-save">
              Save
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Row({ c, onRename, onDelete, onMove }) {
  return (
    <div className="group flex items-center gap-2 rounded-xl px-3 py-3 transition-colors hover:bg-white/[0.035]" data-testid={`chats-row-${c.id}`}>
      <Link to={`/chat/${c.id}`} className="flex min-w-0 flex-1 items-start gap-3" data-testid={`chats-row-link-${c.id}`}>
        <MessageCircle className="mt-0.5 h-4 w-4 shrink-0 text-zinc-600" strokeWidth={1.6} />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[14.5px] text-zinc-100">{c.title}</span>
          {c.project_name && (
            <span className="mt-0.5 inline-flex max-w-full items-center gap-1 truncate text-[11.5px] text-zinc-500" data-testid={`chats-row-project-${c.id}`}>
              <FolderClosed className="h-3 w-3 shrink-0" strokeWidth={1.7} /> {c.project_name}
            </span>
          )}
          {c.snippet && <span className="mt-0.5 line-clamp-2 block text-[12.5px] text-zinc-500">{c.snippet}</span>}
        </span>
        <span className="shrink-0 pt-0.5 text-[12px] text-zinc-600">{formatRelative(c.last_message_at)}</span>
      </Link>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button type="button" aria-label="Conversation actions" data-testid={`chats-row-menu-${c.id}`} className="rounded-md p-1.5 text-zinc-500 opacity-100 hover:bg-white/[0.06] hover:text-white md:opacity-0 md:group-hover:opacity-100 data-[state=open]:opacity-100">
            <MoreHorizontal className="h-4 w-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="border-white/10 bg-ink-850">
          <DropdownMenuItem onSelect={() => onRename(c)} className="gap-2 focus:bg-white/[0.06]" data-testid={`chats-row-rename-${c.id}`}>
            <Pencil className="h-4 w-4" /> Rename
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => onMove(c)} className="gap-2 focus:bg-white/[0.06]" data-testid={`chats-row-move-${c.id}`}>
            <FolderInput className="h-4 w-4" /> Move to project
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => onDelete(c)} className="gap-2 text-rose-300 focus:bg-rose-500/10 focus:text-rose-200" data-testid={`chats-row-delete-${c.id}`}>
            <Trash2 className="h-4 w-4" /> Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export default function ChatsPage() {
  const [q, setQ] = useState("");
  const query = useDebounce(q.trim(), 300);
  const { data, isLoading, isFetching, isError, error, refetch } = useConversations(query, 100);
  const [renaming, setRenaming] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [moving, setMoving] = useState(null);
  const items = data?.items || [];

  return (
    <div className="h-full overflow-y-auto" data-testid="chats-page">
      <div className="mx-auto w-full max-w-[860px] px-4 pb-16 pt-8 sm:px-6 sm:pt-12">
        <div className="flex items-end justify-between gap-4">
          <div>
            <h1 className="text-[1.7rem] font-semibold tracking-tight text-white sm:text-3xl">Chats</h1>
            <p className="mt-1 text-sm text-zinc-500">Every conversation you've had with ChatSleek.</p>
          </div>
          <Link to="/" className="inline-flex h-10 items-center gap-2 rounded-full bg-white px-4 text-sm font-medium text-ink-950 hover:bg-white/90" data-testid="chats-new-chat-button">
            <SquarePen className="h-4 w-4" /> New chat
          </Link>
        </div>
        <div className="mt-8 flex h-12 items-center gap-3 rounded-xl border border-white/[0.09] bg-ink-850 px-4 focus-within:border-white/20">
          <Search className="h-4 w-4 text-zinc-500" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search titles and messages…"
            maxLength={200}
            data-testid="chats-search-input"
            className="h-full flex-1 bg-transparent text-sm text-white placeholder:text-zinc-500 focus:outline-none"
          />
          {isFetching && <Loader2 className="h-4 w-4 animate-spin text-zinc-500" />}
        </div>
        <div className="mt-4 space-y-0.5" data-testid="chats-list">
          {isLoading && [0, 1, 2, 3].map((i) => <div key={i} className="mx-3 my-4 h-4 animate-pulse rounded bg-white/[0.05]" style={{ width: `${80 - i * 10}%` }} />)}
          {isError && (
            <div className="rounded-xl border border-white/10 p-4 text-sm text-zinc-300" data-testid="chats-error">
              {error.message}{" "}
              <button type="button" className="underline underline-offset-4" onClick={() => refetch()}>Try again</button>
            </div>
          )}
          {!isLoading && !isError && items.length === 0 && (
            <div className="flex flex-col items-center py-20 text-center" data-testid={query ? "chats-no-results" : "chats-empty-state"}>
              <MessageCircle className="h-8 w-8 text-zinc-700" strokeWidth={1.4} />
              <p className="mt-4 text-sm text-zinc-300">{query ? `No conversations match “${query}”.` : "No conversations yet."}</p>
              {!query && <p className="mt-1 text-xs text-zinc-500">Start one from Home — it will appear here.</p>}
            </div>
          )}
          {items.map((c) => (
            <Row key={c.id} c={c} onRename={setRenaming} onDelete={setDeleting} onMove={setMoving} />
          ))}
        </div>
      </div>
      {moving && <MoveToProjectDialog conversation={moving} onClose={() => setMoving(null)} />}
      {renaming && <RenameDialog conversation={renaming} onClose={() => setRenaming(null)} />}
      {deleting && <DeleteConversationDialog conversation={deleting} open onOpenChange={(o) => !o && setDeleting(null)} />}
    </div>
  );
}
